import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Button, TextField } from '@mui/material';
import { Clear, Send } from '@mui/icons-material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import * as Emoji from "quill-emoji";
import { useAuth } from '../../../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import { usePost } from '../../../context/PostContext';
import { createPost} from '../../../services/postService';
import CustomAppBar from '../../../components/shared/CustomAppBar';

Quill.register("modules/emoji", Emoji);

interface CreatePostProps {
  messageFromChannel?: string;
  isFromChannel?: boolean;
}

const CreatePostPage: React.FC<CreatePostProps> = ({ messageFromChannel, isFromChannel = false }) => {
  const { userToken, isBroker} = useAuth();
  const { refreshPosts} = usePost();
  const [postContent, setPostContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);  
  const [contact, setContact] = useState({ name: '', type: '', phone: '' });


  const handlePostContentChange = (value: string) => {
    setPostContent(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({ ...contact, [e.target.name]: e.target.value });
  };

  useEffect(() =>{
    if(isFromChannel && messageFromChannel) {
      setPostContent(messageFromChannel);
    }
  },[isFromChannel, messageFromChannel])

  /*
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
  
    input.onchange = async () => {
      if (input.files && input.files[0]) {
        const file = input.files[0];
  
        const formData = new FormData();
        formData.append('file', file); 
  
        try {
          const response = await getImageUrl(formData); 
  
          if (response && response.imageUrl) {
            const quill = quillRef.current?.getEditor();
            const range = quill?.getSelection();
            if (range) {
              quill?.insertEmbed(range.index, 'image', response.imageUrl);
            }
          } else {
            console.error('No se pudo obtener la URL de la imagen.');
          }
        } catch (error) {
          console.error('Error al subir la imagen', error);
        }
      }
    };
  };
*/

const handlePostSubmit = async () => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = postContent;
  const cleanText = tempDiv.innerText.trim();

  if (cleanText === '') {
    toast.error('Por favor, inserta tu publicación.');
    return;
  }

  if (!contact.name || !contact.type || !contact.phone) {
    toast.error('Por favor, ingresa los datos de contacto.');
    return;
  }

  const phoneRegex = /^[0-9]{8,15}$/;
  if (!phoneRegex.test(contact.phone)) {
    toast.error('Por favor, digita un número de teléfono válido.');
    return;
  }

  let postType: string;
  let profileId: string | undefined;

  if (isBroker) {
    const brokerProfile = userToken?.user.profiles.find(profile => profile.roleId === 3);
    postType = 'Oportunidad'; 
    profileId = brokerProfile?.id?.toString(); 
  } else {
    postType = 'Busco'; 
    const userProfile = userToken?.user.profiles.find(profile => profile.roleId === 7);
    profileId = userProfile?.id?.toString(); 
  }

  
  if (!profileId) {
    toast.error('Perfil no encontrado.');
    return;
  }

  const formData = new FormData();
  formData.append('postText', postContent);
  formData.append('postType', postType);
  formData.append('authorProfileId', profileId);
  formData.append('contactName', contact.name);
  formData.append('contactType', contact.type);
  formData.append('contactPhoneNumber', contact.phone);

  setLoading(true);

  try {
    const response = await createPost(formData);
    setLoading(false);

    if (response?.status === 200) {
      setPostContent('');
      toast.success('Publicación creada correctamente.');
      refreshPosts();
      handleClearFields();
    } else {
      toast.error('Hubo un problema al crear la publicación');
    }
  } catch (error) {
    setLoading(false);
    console.error('Error al publicar', error);
    toast.error('Error al crear la publicación');
  }
};

const handleClearFields = () => {
  setPostContent('');
  setContact({ name: '', type: '', phone: '' });
};

  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'], 
        ['emoji'],
      ],
    },
    'emoji-toolbar': true,
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'emoji'];

  return (
    <>
      <CustomAppBar/>
      {/* Contenido */}
      <Container maxWidth="md" sx={{ marginTop: "18vh" }}>
        <Box
          sx={{
            bgcolor: "white",
            boxShadow: 3,
            borderRadius: 2,
            marginTop: 2,
            height: '70vh',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header del cuadro */}
          <Box
            sx={{
              bgcolor: "#e0e0e0",
              padding: 2,
              display: "flex",
              flexWrap: 'wrap', 
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: 22 }}>Crear Publicación</Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2, 
                flexWrap: 'wrap', 
              }}
            >
              <Button
                variant="contained"
                startIcon={<Clear />}
                onClick={handleClearFields}
                sx={{
                  background: "linear-gradient(135deg, #ff4444, #cc0000)",
                  color: "white",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  textTransform: "none",
                  transition: "all 0.3s ease-in-out",
                  boxShadow: "0px 4px 8px rgba(255, 68, 68, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #cc0000, #990000)",
                    transform: "scale(1.05)",
                    boxShadow: "0px 6px 12px rgba(255, 68, 68, 0.4)",
                  },
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handlePostSubmit}
                sx={{
                  background: "linear-gradient(135deg, #007bff, #0056b3)",
                  color: "white",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  textTransform: "none",
                  transition: "all 0.3s ease-in-out",
                  boxShadow: "0px 4px 8px rgba(0, 123, 255, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0056b3, #003c7a)",
                    transform: "scale(1.05)",
                    boxShadow: "0px 6px 12px rgba(0, 123, 255, 0.4)",
                  },
                }}
              >
                {loading ? 'Publicando...' : 'Publicar'}
              </Button>
            </Box>
          </Box>
  
          {/* Área de texto de ReactQuill */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <ReactQuill
              value={postContent}
              onChange={handlePostContentChange}
              modules={modules}
              formats={formats}
              style={{
                fontSize: "16px",
                padding: "10px",
                fontFamily: "Arial, sans-serif",
                backgroundColor: "transparent",
                height: '82%'
              }}
            />
          </Box>
  
          {/* Formulario de contacto */}
          <Box
            sx={{
              padding: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontSize: 20 }}>Información de Contacto</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <TextField
                label="Nombre"
                name="name"
                value={contact.name}
                onChange={handleInputChange}
                size="small"
                fullWidth
              />
              <TextField
                label="Tipo (ej: Vendedor, Comprador, etc.)"
                name="type"
                value={contact.type}
                onChange={handleInputChange}
                size="small"
                fullWidth
              />
              <TextField
                label="Teléfono"
                type='tel'
                name="phone"
                value={contact.phone}
                onChange={handleInputChange}
                size="small"
                fullWidth
              />
            </Box>
          </Box>
        </Box>
        <ToastContainer />
      </Container>
    </>
  );
};

export default CreatePostPage;
